/**
 * Mobile Bottom Sheet for map settings
 */

import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { MapContent } from './MapContent';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription
} from '@/components/ui/drawer';

interface GeographicEntityWithRelevance extends GeographicEntity {
    relevanceType?: string;
    relevanceReason?: string;
}

interface EventMarkerData {
    event: HistoricalEventByPassage | HistoricalEvent;
    position: unknown;
    matchingPlace: GeographicEntity;
}

interface MapFiltersPanelProps {
    isOpen: boolean;
    onClose: () => void;
    selectedStyleId: string;
    onStyleChange: (styleId: string) => void;
    showLabels: boolean;
    onShowLabelsChange: (show: boolean) => void;
    showAncientRoads: boolean;
    onShowAncientRoadsChange: (show: boolean) => void;
    showSeaLanes: boolean;
    onShowSeaLanesChange: (show: boolean) => void;
    showRomanProvinces: boolean;
    onShowRomanProvincesChange: (show: boolean) => void;
    showAncientEmpires: boolean;
    onShowAncientEmpiresChange: (show: boolean) => void;
    passageEventsOnly: boolean;
    onPassageEventsOnlyChange: (show: boolean) => void;
    availablePlaceTypes: string[];
    selectedPlaceTypes: Set<string>;
    onPlaceTypesChange: (types: Set<string>) => void;
    placeTypeCounts: Map<string, number>;
    availableEventCategories: string[];
    selectedEventCategories: Set<string>;
    onEventCategoriesChange: (categories: Set<string>) => void;
    eventCategoryCounts: Map<string, number>;
    visiblePlaces: GeographicEntityWithRelevance[];
    eventMarkers: EventMarkerData[];
    onPlaceClick: (placeName: string, coordinates: { latitude: number; longitude: number }) => void;
    onEventClick: (eventName: string, coordinates: { latitude: number; longitude: number }) => void;
    passageYear?: number | null;
}

export function MapFiltersPanel(props: MapFiltersPanelProps) {
    const isDarkMode = useDarkMode();

    // Safety check: Don't render the bottom sheet on desktop
    // (Desktop uses the persistent sidebar instead)
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
    if (isDesktop && props.isOpen) {
        return null;
    }

    return (
        <Drawer open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
            {/* hideHandle={true} removes the drag bar to simplify the UI per feedback */}
            <DrawerContent
                hideHandle={true}
                className={isDarkMode ? "bg-black/95 text-white border-white/20" : "bg-white text-slate-900"}
            >
                <DrawerHeader className="border-b border-border/50 pb-4 flex flex-row items-center justify-between">
                    <div className="flex flex-col gap-0.5 text-left">
                        <DrawerTitle className="text-xs font-bold uppercase tracking-[0.2em] opacity-60">
                            Map Settings
                        </DrawerTitle>
                        <DrawerDescription className="sr-only">
                            Configure map styles, filters, and view options
                        </DrawerDescription>
                    </div>

                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={props.onClose}
                        className="h-8 w-8 rounded-full opacity-70 hover:opacity-100"
                    >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Close</span>
                    </Button>
                </DrawerHeader>

                <div className="overflow-y-auto max-h-[65vh] pb-10 px-4">
                    <MapContent {...props} />
                </div>
            </DrawerContent>
        </Drawer>
    );
}
